import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getLoginMessage, getMergeAccountMessage, getProfileDetails, getProfileLeaderboard, getProfileSummary, getPublicLeaderboard, getPublicProfileLeaderboard, getPublicProfileSummary, getSetClaimMessage, logIn, logOut, mergeAccount, setClaimWallet, updateProfileData, UserUnauthorizedError, } from '../api/mod';
import { getErrorMessage, throwIfAborted } from './helpers';
import { memoizeThunk } from './utils';
// To learn more about this pattern, check https://github.com/rango-exchange/app-v2/pull/281
export function createSignMessage() {
    const actionType = 'profile/signMessage';
    const signMessageThunk = memoizeThunk(actionType, () => createAsyncThunk(actionType, async (params, options) => {
        const { rejectWithValue, getState, dispatch, signal } = options;
        const { account, getSigners, callback, errorCallback } = params;
        const { meta: { blockchains }, profile: { profileAuth, isLoggedIn }, } = getState();
        const { numberOfRetries } = profileAuth;
        throwIfAborted(signal);
        const signer = await getSigners(account.walletType);
        const blockchainMeta = blockchains.find((blockchain) => blockchain.name === account.blockchain);
        if (!blockchainMeta) {
            return rejectWithValue('Something went wrong. Please try again.');
        }
        let signAction;
        let saveAction;
        if (isLoggedIn) {
            signAction = getMergeAccountMessage;
            saveAction = mergeAccount;
        }
        else {
            signAction = getLoginMessage;
            saveAction = logIn;
        }
        try {
            throwIfAborted(signal);
            const { message, messageId } = await signAction({
                address: account.address,
                blockchain: account.blockchain,
            }, signal);
            throwIfAborted(signal);
            const signature = await signer
                .getSigner(blockchainMeta.type)
                .signMessage(message, account.address, blockchainMeta.chainId);
            throwIfAborted(signal);
            const { profile } = await saveAction({ messageId, signature }, signal);
            if (profile) {
                callback?.(profile, { actionType: isLoggedIn ? 'merge' : 'login' });
            }
            return profile;
        }
        catch (error) {
            if (signal.aborted || axios.isCancel(error)) {
                return null;
            }
            const shouldLogOutAndRetry = !isLoggedIn && error instanceof UserUnauthorizedError && numberOfRetries === 0;
            if (shouldLogOutAndRetry) {
                await logOut(signal);
                // Use a timeout to dispatch signMessage after incrementing the numberOfRetries.
                setTimeout(async () => dispatch(signMessageThunk()(params)), 0);
                return null;
            }
            errorCallback?.(error);
            return rejectWithValue(getErrorMessage(error));
        }
    }));
    return signMessageThunk();
}
export function createFetchProfileSummary() {
    const actionType = 'profile/fetchProfileSummary';
    const fetchProfileSummaryThunk = memoizeThunk(actionType, () => createAsyncThunk(actionType, async (params, options) => {
        const { rejectWithValue, signal, getState, fulfillWithValue, dispatch } = options;
        const state = getState().profile;
        const publicProfile = params.public ||
            !state.isLoggedIn ||
            (state.isLoggedIn && state.username !== params.username);
        const request = publicProfile
            ? getPublicProfileSummary.bind(null, params.username)
            : getProfileSummary;
        try {
            const profileSummary = await request(signal);
            return fulfillWithValue(profileSummary.profile);
        }
        catch (error) {
            if (error instanceof UserUnauthorizedError) {
                void dispatch(fetchProfileSummaryThunk()({ username: params.username, public: true }));
                return rejectWithValue(null);
            }
            return rejectWithValue(getErrorMessage(error));
        }
    }));
    return fetchProfileSummaryThunk();
}
export function createSetClaim() {
    const actionType = 'profile/setClaim';
    const setClaimThunk = memoizeThunk(actionType, () => createAsyncThunk(actionType, async (params, options) => {
        const { rejectWithValue, getState, signal } = options;
        const { account, getSigners } = params;
        const { meta: { blockchains }, } = getState();
        throwIfAborted(signal);
        const signer = await getSigners(account.walletType);
        const blockchainMeta = blockchains.find((blockchain) => blockchain.name === account.blockchain);
        if (!blockchainMeta) {
            return rejectWithValue(`We couldn't find ${account.blockchain} in our initial data. Please try again.`);
        }
        try {
            throwIfAborted(signal);
            const { message, messageId } = await getSetClaimMessage({
                address: account.address,
                blockchain: account.blockchain,
            }, signal);
            throwIfAborted(signal);
            const signature = await signer
                .getSigner(blockchainMeta.type)
                .signMessage(message, account.address, blockchainMeta.chainId);
            throwIfAborted(signal);
            const { profile } = await setClaimWallet({ messageId, signature }, signal);
            return profile;
        }
        catch (error) {
            if (signal.aborted || axios.isCancel(error)) {
                return null;
            }
            return rejectWithValue(getErrorMessage(error));
        }
    }));
    return setClaimThunk();
}
export function createRequestLogOutUser() {
    const actionType = 'profile/logOut';
    const logOutThunk = memoizeThunk(actionType, () => createAsyncThunk(actionType, async (_, options) => {
        const { rejectWithValue, signal } = options;
        try {
            await logOut(signal);
        }
        catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    }));
    return logOutThunk();
}
export function createProfileThunks() {
    const fetchProfileDetails = createAsyncThunk('profile/fetchProfileDetails', async (_, options) => {
        const { rejectWithValue, signal } = options;
        try {
            const profileDetails = await getProfileDetails(signal);
            return profileDetails.profile;
        }
        catch (error) {
            if (error instanceof UserUnauthorizedError) {
                return rejectWithValue(null);
            }
            return rejectWithValue(getErrorMessage(error));
        }
    });
    const fetchProfileLeaderboard = createAsyncThunk('profile/fetchProfileLeaderboard', async (params, options) => {
        const { rejectWithValue, signal, getState, dispatch } = options;
        const state = getState().profile;
        const publicProfile = params.public ||
            !state.isLoggedIn ||
            (state.isLoggedIn && state.username !== params.username);
        const request = publicProfile
            ? getPublicProfileLeaderboard.bind(null, params.username)
            : getProfileLeaderboard;
        try {
            const profileLeaderboard = await request(signal);
            return profileLeaderboard.data;
        }
        catch (error) {
            if (error instanceof UserUnauthorizedError) {
                void dispatch(fetchProfileLeaderboard({ username: params.username, public: true }));
                return rejectWithValue(null);
            }
            return rejectWithValue(getErrorMessage(error));
        }
    });
    const fetchPublicLeaderboard = createAsyncThunk('profile/fetchPublicLeaderboard', async (params, options) => {
        const { rejectWithValue, signal } = options;
        try {
            const leaderboard = await getPublicLeaderboard(params, signal);
            return { leaderboard: leaderboard.data, totalPages: leaderboard.totalPages };
        }
        catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    });
    const fetchTopPublicLeaderboard = createAsyncThunk('profile/fetchTopPublicLeaderboard', async (params, options) => {
        const { rejectWithValue, signal } = options;
        try {
            const leaderboard = await getPublicLeaderboard({ type: params.type, page: 0, offset: 10 }, signal);
            return leaderboard.data;
        }
        catch (error) {
            return rejectWithValue(getErrorMessage(error));
        }
    });
    const updateUsername = createAsyncThunk('profile/updateUsername', async (params, options) => {
        const { rejectWithValue, signal } = options;
        const { userName, callback } = params;
        const DEFAULT_ERROR_MESSAGE = 'An error occurred while updating the username.';
        try {
            const result = await updateProfileData({ userName }, signal);
            if (result.success) {
                callback();
                return { username: userName };
            }
            return rejectWithValue(DEFAULT_ERROR_MESSAGE);
        }
        catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE);
        }
    });
    return {
        signMessage: createSignMessage(),
        fetchProfileSummary: createFetchProfileSummary(),
        fetchProfileDetails,
        fetchProfileLeaderboard,
        fetchPublicLeaderboard,
        fetchTopPublicLeaderboard,
        updateUsername,
        requestLogOutUser: createRequestLogOutUser(),
        setClaim: createSetClaim(),
    };
}
